<template>
  <div
    class="container">
    <div class="logo">
      <img src="@/assets/logo-black-no-text.png" />
      <div class="logo-text">Byblio</div>
    </div>
    <div v-if="initialRegistrationDone">
      <span
        class="message-top">
        Welcome to Byblio!
      </span>
      <div
        class="personal-details">
        <b-field
          label="How should we call you?"
          message="Optional. If not provided, we will call you by the names provided below or by your email address.">
          <b-input
            class="input-field"
            v-model="name"
            placeholder="Name or nickname" />
        </b-field>
        <b-field
          label="Enter your first and / or last name">
          <b-input
            class="input-field"
            v-model="firstName"
            placeholder="First name" />
        </b-field>
        <b-field
          message="Both are optional.">
          <b-input
            class="input-field"
            v-model="lastName"
            placeholder="Last name" />
        </b-field>
        <b-field
          label="Email"
          message="This is the email address that you just registered with. It cannot be changed at this point.">
          <b-input :value="$auth.user.email" disabled />
        </b-field>
      </div>
      <div class="continue">
        <button
          class="button is-secondary-dark"
          @click="submitPersonalDetails">
          <i class="mdi mdi-loading mdi-spin" style="marginRight: 0.5rem" v-show="waitingToContinue" />Continue
        </button>
      </div>
    </div>
    <div v-else style="paddingTop: 3rem">
      <i class="mdi mdi-loading mdi-spin mdi-36px" />
    </div>

    <div class="error" v-if="error">
      <span style="color: red">{{ error }}</span>
      <br>
      <button
        class="button is-primary"
        @click="$navigateTo({ name: 'AppView' })">
        Ignore and continue to Byblio
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'RegisterUser',
  data() {
    return {
      name: null,
      firstName: null,
      lastName: null,
      error: null,
      verificationCode: null,
      emailSend: false,
      emailVerified: false,
      initialRegistrationSubmitted: false,
      initialRegistrationDone: false,
      waitingToContinue: false,
    };
  },
  computed: {
    ...mapState({
      collections: state => state.collections.collectionNodes,
    }),
    authData: function() { // eslint-disable-line 
      return this.$auth.user.email;
    },
  },
  methods: {
    ...mapActions({
      getDataAtSetup: 'users/getDataAtSetup',
      registerUser: 'users/registerUser',
      updateUser: 'users/updateUser',
    }),
    submitPersonalDetails() {
      this.waitingToContinue = true;

      const updateObj = {
        name: this.name,
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.authData,
      };
      this.updateUser(updateObj)
        .then(async () => {
          await this.getDataAtSetup();

          // Since updateUser fulfills the promise before the db is updated, getDataAtSetup returns the old user data. Hence, here we commit the previous update again
          // (only Vuex should be updated, the db is updated but just later than the getDataAtSetup request arrival).
          this.$store.commit('users/SetUserInfo', updateObj);

          // Go to appView
          const collectionId = this.collections['0'].children[0].id;
          this.$goToCollection({ id: collectionId });
          this.waitingToContinue = false;
        })
        .catch((err) => {
          console.error(err);
          this.error = 'Error: Could not update your information!';
        });
    },
    async submitInitialRegistration() {
      if (!this.$auth.user.email) {
        this.error = 'Cannot register at the moment';
        return;
      }

      const currentUTC = this.$getCurrentUTC();
      const registrationObj = {
        email: this.$auth.user.email,
        auth0UserId: this.$auth.user.sub,
        accountRole: 'admin',
        collectionAccessLevel: 1,
        planLevel: 'individual',
        currentUTC,
      };

      // Send initial registration
      try {
        await this.registerUser(registrationObj);
      }
      catch (err) {
        if (err.message === 'User already exists') {
          await this.getDataAtSetup();
          const collectionId = this.collections['0'].children[0].id;
          this.$goToCollection({ id: collectionId });
        }
        else {
          this.error = 'Oops, something went wrong. Try again or contact Anne Albert!';
        }
      }
      finally {
        this.initialRegistrationDone = true;
      }
    },
  },
  watch: {
    authData: function() { // eslint-disable-line
      if (this.initialRegistrationSubmitted) return;
      this.initialRegistrationSubmitted = true;
      this.submitInitialRegistration();
    },
  },
  created() {
    // In case auth is already loaded, the watcher above won't execute. Execute stuff here if auth is already available
    if (this.authData) {
      this.initialRegistrationSubmitted = true;
      this.submitInitialRegistration();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/fonts';
@import '~styles/colors';

.container {
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 4rem;
  width: 100vw;
  padding: 0.5rem;
  border-bottom: 1px solid $primary;
}
.logo img {
  height: 2.5rem;
}
.logo-text {
  display: inline-block;
  height: 2.5rem;
  position: relative;
  font-size: 1.2rem;
  line-height: 2.5rem;
  letter-spacing: 2px;
  left: 0.5rem;
  font-family: $quattrocento;
}
.message-top {
  font-size: 1.5rem;
  display: block;
  padding-top: 1.5rem;
  font-family: $headerFont;
  text-align: center;
}
.verify-email, .personal-details {
  padding: 2rem;
  width: 35vw;
  text-align: unset;
}
.input-field {
  width: 30vw;
  margin: auto;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}
.optional {
  font-size: 0.65rem;
  font-style: italic;
}
.continue {
  text-align: center;
  padding-top: 0.5rem;
}
.error {
  margin-top: 1rem;
  text-align: center;
}
</style>
